import { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Typography, CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getQueryResultStatus, getQueryResultTotal, useQueryResult } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useQueryResultRefresh } from '../../../../hooks/QueryBuilder/useQueryResultRefresh';

const POLL_INTERVAL_MS = 4000;

interface CountWaitingDialogProps {
  open: boolean;
  queryId: number | null;
  description: string;
  onCompleted: (count: number | null) => void;
  onClose: () => void;
}

function CountWaitingDialog({ open, queryId, description, onCompleted, onClose }: CountWaitingDialogProps) {
  const { fetchQueryResult, result, reset } = useQueryResult();
  const completedRef = useRef(false);
  const watchedQueryId = open ? queryId : null;
  const status = getQueryResultStatus(result);
  const isError = status === 'error' || status === 'expired';
  const isCompleted = status === 'completed';

  useEffect(() => {
    if (watchedQueryId === null) return;
    completedRef.current = false;
    reset();
    fetchQueryResult(watchedQueryId);
  }, [watchedQueryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useQueryResultRefresh(
    watchedQueryId,
    () => { if (watchedQueryId !== null) fetchQueryResult(watchedQueryId, { keepPreviousResult: true }); },
    POLL_INTERVAL_MS,
  );

  useEffect(() => {
    if (!open || !isCompleted || completedRef.current) return;
    completedRef.current = true;
    onCompleted(getQueryResultTotal(result));
  }, [open, isCompleted, result]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={isError ? onClose : undefined} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, pt: 4, pb: 2 }}>
        {isError ? (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
            <Typography variant="subtitle1" fontWeight={700}>Le comptage a échoué</Typography>
            <Typography variant="body2" color="text.secondary">
              {result?.message || 'Le résultat du comptage est indisponible. Veuillez relancer le comptage.'}
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={48} />
            <Typography variant="subtitle1" fontWeight={700}>Comptage en cours…</Typography>
            <Typography variant="body2" color="text.secondary">
              Nous calculons le nombre de destinataires de la requête
              {description ? <> <strong>« {description} »</strong></> : null}.
              Cette opération peut prendre quelques instants, merci de patienter.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          {isError ? 'Fermer' : 'Continuer en arrière-plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CountWaitingDialog;
